import './styles.css';

import { NavMap } from './map';
import { mountUi } from './ui';
import type { HudData } from './ui';
import { fetchAmenitiesAlongRoute, pickNextAmenities } from './amenities';
import type { Amenity } from './amenities';
import { loadSavedLocation, saveLocation, loadSavedViewMode, saveViewMode, loadSavedRouteOptions, saveRouteOptions, pushRecentDestination, setFavoriteSlot, addSavedPlace, removeSavedPlace, renameSavedPlace, placeDisplayName, loadLastRoute, saveLastRoute, loadSavedPowerSaver, savePowerSaver, loadPlanRouteCache, savePlanRouteCache } from './storage';
import type { SavedPlace } from './storage';
import { config } from './config';
import {
  Rerouter,
  getRoute,
  planRoutes,
  distanceToRouteMeters,
  findCurrentStep,
  smaBearing,
  haversineMeters,
  MAX_ROUTE_STOPS,
} from './routing';
import {
  NAV_GPS_COAST_MAX_MS,
  NAV_GPS_ON_ROUTE_M,
  NAV_GPS_STALE_MS,
  clampOnRouteTraveledMeters,
  coastAdvanceMeters,
  shouldAcceptNavGpsFix,
  type NavGpsSample,
} from './navGps';
import { thinLatLng } from './geoSimplify';
import { scheduleIdle } from './mainThread';
import type { GeocodeHit } from './geocoding';
import { geocode, getGeocodeCacheDebug, getGeocodeQuotaDebug, reverseGeocode, setGeocodeFocus } from './geocoding';
import { t } from './i18n';
import type { LatLng, Profile, RouteOptions, RouteResult, ViewMode } from './types';
import {
  fetchNearbyPois,
  extractRadars,
  extractNearestSpeedLimit,
  pickRelevantRadar,
  extractRoadworks,
  pickRelevantRoadwork,
} from './poi';
import {
  fetchCurrentWeather,
  formatTempC,
  formatWindKmh,
  weatherNeedsRefresh,
  windTowardDeg,
  type WeatherSnapshot,
} from './weather';
import { bindNavWakeLockVisibility, setNavWakeLock } from './wakeLock';

/**
 * Application bootstrap.
 *
 * - Builds the Leaflet map
 * - Wires UI events to the routing/geocoding layers
 * - Starts geolocation tracking + automatic rerouting
 * - Drives the bottom HUD strip with live speed / remaining / ETA while
 *   a navigation session is active
 *
 * All long-lived mutable state (current profile, destination, in-flight
 * rerouter, last route result) is captured by closures — no globals.
 */

async function bootstrap(): Promise<void> {
  const mapEl = document.getElementById('map');
  if (!mapEl) throw new Error('Missing #map element');

  const ui = mountUi();
  let lastFix: LatLng | null = loadSavedLocation();

  const WEATHER_INTERVAL_MS = 10 * 60_000;
  let lastWeather: WeatherSnapshot | null = null;
  let weatherAbort: AbortController | null = null;
  let weatherMoveTimer: number | null = null;
  /** Destination wins; otherwise map centre; else GPS / default. Filled in after map boot. */
  let resolveWeatherFocus: () => LatLng = () =>
    lastFix ??
    loadSavedLocation() ?? {
      lat: config.defaultCenter.lat,
      lng: config.defaultCenter.lng,
    };

  function weatherFocus(): LatLng {
    return resolveWeatherFocus();
  }

  function paintWeather(snap: WeatherSnapshot): void {
    ui.setWeather({
      tempLabel: formatTempC(snap.tempC),
      windLabel: formatWindKmh(snap.windKmh),
      windTowardDeg: windTowardDeg(snap.windFromDeg),
    });
  }

  function scheduleWeatherRefresh(here: LatLng): void {
    if (!weatherNeedsRefresh(lastWeather, here, Date.now(), WEATHER_INTERVAL_MS)) {
      return;
    }
    void refreshWeather(here);
  }

  async function refreshWeather(here: LatLng): Promise<void> {
    weatherAbort?.abort();
    const ctl = new AbortController();
    weatherAbort = ctl;
    try {
      const snap = await fetchCurrentWeather(here, ctl.signal);
      if (ctl.signal.aborted) return;
      lastWeather = snap;
      paintWeather(snap);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      if (!lastWeather) ui.setWeather(null);
    } finally {
      if (weatherAbort === ctl) weatherAbort = null;
    }
  }

  ui.setWeather(null);
  void refreshWeather(weatherFocus());
  window.setInterval(() => {
    void refreshWeather(weatherFocus());
  }, WEATHER_INTERVAL_MS);

  const map = new NavMap(mapEl);
  map.onFollowChange((following) => ui.setAutoZoomActive(following));
  map.onMapBearingChange((bearingDeg) => ui.setArrowBearing(bearingDeg));
  map.onLongPress((point) => {
    if (replanning) return;
    void (async () => {
      ui.setStatus(t('status.mapPickPlanning'));
      let label: string;
      try {
        label = await reverseGeocode(point);
      } catch {
        label = `${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}`;
      }
      applyPickedPlace({ location: point, label });
    })();
  });
  ui.setAutoZoomActive(true);
  ui.on('layout', () => map.resize());
  ui.on('panelToggle', () => {
    map.resize();
    if (isNavigating || isPaused) return;
    const r = lastRouteResult;
    if (r && r.coords.length >= 2) {
      window.requestAnimationFrame(() => fitRouteLikeOverview(r.coords));
    }
  });
  map.onAlternativeSelect((index) => {
    selectAlternativeRoute(index);
  });

  // Centre-on-boot priority: saved-location (sync) > GPS (async) > config.
  // Applied synchronously so the map never flashes blank; the asynchronous
  // initial getCurrentPosition below will overwrite this via setUserLocation
  // → panTo as soon as GPS is ready. The wrapper queues the jump if the
  // map style is still loading.
  const savedLoc = loadSavedLocation();
  if (savedLoc) {
    map.jumpToInitialView(savedLoc, config.defaultZoom);
    setGeocodeFocus(savedLoc);
  }

  let currentProfile: Profile = 'driving-car';
  let routeOptions: RouteOptions = loadSavedRouteOptions();
  type RouteStop = { location: LatLng; label: string };
  let stops: RouteStop[] = [];
  let addingStop = false;
  let searchPickGeneration = 0;
  let replanning = false;
  let planAbort: AbortController | null = null;
  let planGeneration = 0;
  let isNavigating = false;
  /** True while a navigation session is paused — the watchPosition is stopped
   *  but the route + destination + HUD snapshot are preserved. */
  let isPaused = false;

  resolveWeatherFocus = () => {
    const dest = stops[stops.length - 1];
    if (dest) return dest.location;
    return (
      map.getCenter() ??
      lastFix ??
      loadSavedLocation() ?? {
        lat: config.defaultCenter.lat,
        lng: config.defaultCenter.lng,
      }
    );
  };

  map.onMoveEnd(() => {
    if (weatherMoveTimer !== null) window.clearTimeout(weatherMoveTimer);
    weatherMoveTimer = window.setTimeout(() => {
      weatherMoveTimer = null;
      scheduleWeatherRefresh(weatherFocus());
    }, 450);
  });

  /** In-memory + localStorage routes for a trip (profile × options × stops). */
  type ProfileRouteCacheEntry = {
    key: string;
    start: LatLng;
    route: RouteResult;
    /** Preference variants (fastest/shortest/…) for overview; memory only. */
    alternatives: RouteResult[];
    /** false = restored from disk (thinned); full ORS still needed. */
    complete: boolean;
  };
  const PROFILE_ROUTE_CACHE_MAX = 6;
  const PROFILE_ROUTE_START_TOLERANCE_M = 250;
  let profileRouteCache: ProfileRouteCacheEntry[] = loadPlanRouteCache().map((e) => ({
    key: e.key,
    start: e.start,
    route: e.route,
    alternatives: [],
    complete: false,
  }));

  function profileRouteCacheKey(
    profile: Profile,
    options: RouteOptions,
    tripStops: RouteStop[],
  ): string {
    const stopsPart = tripStops
      .map((s) => `${s.location.lat.toFixed(5)},${s.location.lng.toFixed(5)}`)
      .join(';');
    const avoid = [...options.avoid].sort().join(',');
    return `${profile}|${options.preference}|${avoid}|${stopsPart}`;
  }

  function persistProfileRouteCache(): void {
    scheduleIdle(() => {
      savePlanRouteCache(
        profileRouteCache.map((e) => ({
          key: e.key,
          start: e.start,
          route: e.route,
          savedAt: Date.now(),
        })),
      );
    }, 1_500);
  }

  function clearProfileRouteCache(): void {
    profileRouteCache = [];
  }

  function readProfileRouteCache(
    profile: Profile,
    options: RouteOptions,
    tripStops: RouteStop[],
    start: LatLng | null,
    opts: { ignoreStartDrift?: boolean } = {},
  ): ProfileRouteCacheEntry | null {
    const key = profileRouteCacheKey(profile, options, tripStops);
    let hit = profileRouteCache.find((e) => e.key === key);
    if (!hit) {
      const fromDisk = loadPlanRouteCache().find((e) => e.key === key);
      if (fromDisk) {
        hit = {
          key: fromDisk.key,
          start: fromDisk.start,
          route: fromDisk.route,
          alternatives: [],
          complete: false,
        };
        profileRouteCache = profileRouteCache.filter((e) => e.key !== key);
        profileRouteCache.push(hit);
        while (profileRouteCache.length > PROFILE_ROUTE_CACHE_MAX) {
          profileRouteCache.shift();
        }
      }
    }
    if (!hit) return null;
    if (
      !opts.ignoreStartDrift &&
      start &&
      haversineMeters(hit.start, start) > PROFILE_ROUTE_START_TOLERANCE_M
    ) {
      return null;
    }
    return hit;
  }

  function writeProfileRouteCache(
    profile: Profile,
    options: RouteOptions,
    tripStops: RouteStop[],
    start: LatLng,
    route: RouteResult,
    complete = true,
    alternatives: RouteResult[] = [],
  ): void {
    const key = profileRouteCacheKey(profile, options, tripStops);
    profileRouteCache = profileRouteCache.filter((e) => e.key !== key);
    profileRouteCache.push({ key, start, route, alternatives, complete });
    while (profileRouteCache.length > PROFILE_ROUTE_CACHE_MAX) {
      profileRouteCache.shift();
    }
    persistProfileRouteCache();
  }

  /** Apply reduced cache instantly, then fill in full ORS geometry/steps. */
  async function completeCachedPlanInBackground(
    start: LatLng,
    dest: RouteStop,
    profile: Profile,
    options: RouteOptions,
    tripStops: RouteStop[],
    signal: AbortSignal,
    generation: number,
    planOpts: { forceOverview?: boolean; persist?: boolean } = {},
  ): Promise<void> {
    try {
      const planned = await planRoutes(
        start,
        tripStops.map((s) => s.location),
        profile,
        options,
        signal,
        { alternatives: !isNavigating && !isPaused },
      );
      if (signal.aborted || generation !== planGeneration) return;
      writeProfileRouteCache(
        profile,
        options,
        tripStops,
        start,
        planned.primary,
        true,
        planned.alternatives,
      );
      applyPlannedRoute(planned.primary, dest, profile, planOpts, planned.alternatives);
    } catch (err) {
      if (isAbortError(err) || signal.aborted || generation !== planGeneration) {
        return;
      }
    }
  }

  function syncAlternativeRoutesOnMap(): void {
    if (isNavigating || isPaused || routeAlternatives.length === 0) {
      map.clearAlternativeRoutes();
      return;
    }
    map.setAlternativeRoutes(routeAlternatives.map((r) => r.coords));
  }

  function selectAlternativeRoute(index: number): void {
    if (isNavigating || isPaused) return;
    const dest = primaryDestination();
    if (!dest || !lastRouteResult) return;
    if (index < 0 || index >= routeAlternatives.length) return;
    const chosen = routeAlternatives[index]!;
    const nextAlts = [lastRouteResult, ...routeAlternatives.filter((_, i) => i !== index)];
    const start = lastFix ?? loadSavedLocation();
    applyPlannedRoute(chosen, dest, currentProfile, { forceOverview: true }, nextAlts);
    if (start) {
      writeProfileRouteCache(
        currentProfile,
        routeOptions,
        stops,
        start,
        chosen,
        true,
        nextAlts,
      );
    }
  }

  function applyPlannedRoute(
    route: RouteResult,
    dest: RouteStop,
    profile: Profile,
    planOpts: { forceOverview?: boolean; persist?: boolean } = {},
    alternatives?: RouteResult[],
  ): void {
    lastRouteResult = route;
    if (alternatives !== undefined) {
      routeAlternatives = alternatives;
    }
    if (isNavigating) {
      map.clearAlternativeRoutes();
      map.drawRoute(route.coords, { fit: false, keepFollow: true });
      if (planOpts.forceOverview) fitRouteLikeOverview(route.coords);
    } else {
      map.drawRoute(route.coords, { fit: false });
      syncAlternativeRoutesOnMap();
      fitRouteLikeOverview(route.coords);
    }
    bindActiveRoute(route.coords);
    ui.setRoute(route.coords, route.distance, route.duration, route.steps);
    ui.setNavState(isNavigating ? 'navigating' : 'planned');
    const summary =
      stops.length > 1
        ? t('status.multiStopReady')
        : `${t('status.destination')}${dest.label}`;
    ui.setStatus(
      isNavigating ? `${t('status.destinationUpdated')}${dest.label}` : summary,
    );
    if (!isNavigating) syncStopsChrome();
    if (planOpts.persist === false) return;
    const place: SavedPlace = {
      lat: dest.location.lat,
      lng: dest.location.lng,
      label: dest.label,
    };
    pushRecentDestination(place);
    const cachePayload = {
      destination: place,
      route: {
        coords: thinLatLng(route.coords, 6_000),
        distance: route.distance,
        duration: route.duration,
        steps: route.steps,
      },
      profile,
      savedAt: Date.now(),
    };
    scheduleIdle(() => {
      try {
        saveLastRoute(cachePayload);
        ui.refreshFavorites();
      } catch {
        /* ignore */
      }
    }, 2_500);
  }

  function syncSessionWakeLock(): void {
    void setNavWakeLock(isNavigating || isPaused);
  }

  bindNavWakeLockVisibility(syncSessionWakeLock);
  /** The last snapshot pushed into the HUD. Frozen on pause so the user
   *  still sees a believable ETA / distance / next-step while paused. */
  let lastHudData: HudData | null = null;
  /**
   * Latest successful ORS route result. Used by the onPosition HUD tick to
   * compute a fallback average speed when GPS speed is unavailable
   * (stopped at a light, or desktop browsers report speed = null).
   */
  let lastRouteResult: RouteResult | null = null;
  /** ORS alternative routes for the current planned trip (overview only). */
  let routeAlternatives: RouteResult[] = [];
  /**
   * Currently selected view mode (the 2-state 2D ⇆ Tilted toggle). The
   * source of truth lives in main.ts; ui.ts mirrors it for visual layout
   * only (topbar buttons + body[data-view-mode] selector).
   */
  let viewMode: ViewMode = 'north';
  /**
   * Smoothed compass heading in degrees (0..360). During live nav this
   * tracks the route tangent (EMA); defaults to 0 = north.
   */
  let smoothBearingDeg = 0;
  /** Timestamp of last POI (cameras + speed limit) refresh. */
  let lastPoiRefreshAt = 0;
  /** Active road's posted speed limit (km/h). `null` when no data. */
  let currentSpeedLimitKmh: number | null = null;
  /** Anchor of the last matched maxspeed way — used to keep the badge sticky. */
  let lastSpeedLimitAt: LatLng | null = null;
  /**
   * Wall-clock time the speed first crossed (limit + 10 km/h). The
   * over-speed warning fires after ≥3 s sustained over-speed so sparse
   * GPS cadence (1–4 s) does not flicker when the speed briefly dips
   * during a hill crest. `null` while below the threshold.
   */
  let overspeedSinceMs: number | null = null;

  // Single source of truth for GPS error messages. Used by planAndRender
  // and the always-on watchPosition stream (including live navigation).
  //
  // PERMISSION_DENIED is intentionally NOT mapped here — gpsErrorLabel()
  // emits a special instructive message (`status.gpsDeniedHelp`) instead,
  // because the short "denied — enable in browser settings" string left
  // phone-PWA users with no clear next step. This applies to every
  // PERMISSION_DENIED path: the pre-flight probe (Safari fallback), the
  // getCurrentPosition error callback, and the watchPosition error stream
  // during an active navigation session.
  const GPS_ERROR_LABELS = new Map<number, string>([
    [GeolocationPositionError.POSITION_UNAVAILABLE, t('status.gpsUnavailable')],
    [GeolocationPositionError.TIMEOUT, t('status.gpsTimeout')],
  ]);
  const gpsErrorLabel = (err: GeolocationPositionError): string => {
    if (err.code === GeolocationPositionError.PERMISSION_DENIED) {
      return t('status.gpsDeniedHelp');
    }
    const known = GPS_ERROR_LABELS.get(err.code);
    if (known) return known;
    return err.message ? `${t('status.gpsError')}${err.message}` : t('status.gpsError').trim();
  };

  function primaryDestination(): RouteStop | null {
    return stops[stops.length - 1] ?? null;
  }

  function syncStopsChrome(): void {
    map.setStops(
      stops.map((s) => ({ location: s.location, label: s.label })),
      { center: false },
    );
    ui.setStopsUi(stops.map((s) => ({ label: s.label })), {
      addingStop,
      canAdd: stops.length > 0 && stops.length < MAX_ROUTE_STOPS,
    });
  }

  function endActiveNavForNewPlan(): void {
    if (!isNavigating && !isPaused) return;
    stopNavCoast();
    lastGoodNavGps = null;
    rerouter.stop();
    isNavigating = false;
    isPaused = false;
    map.setLiveNavigation(false);
    lastHudData = null;
  }

  /** Wipe route, pins, POIs, and live nav so a new destination starts clean. */
  function resetActiveTrip(): void {
    stopNavCoast();
    lastGoodNavGps = null;
    planAbort?.abort();
    planAbort = null;
    planGeneration += 1;
    replanning = false;
    stops = [];
    addingStop = false;
    lastRouteResult = null;
    routeAlternatives = [];
    lastHudData = null;
    currentSpeedLimitKmh = null;
    lastSpeedLimitAt = null;
    overspeedSinceMs = null;
    cachedAmenities = [];
    clearProfileRouteCache();
    map.clearRoute();
    map.clearCameras();
    map.clearRoadworks();
    map.clearAmenities();
    ui.setHudAmenities({});
    bindActiveRoute([]);
    rerouter.stop();
    isNavigating = false;
    isPaused = false;
    map.setLiveNavigation(false);
    ui.setSpeedLimit(null);
    ui.setHudRadar(null);
    ui.setRoadwork(null, null);
    ui.setOverspeed(false);
    syncSessionWakeLock();
  }

  function applyPickedPlace(
    place: RouteStop,
    opts: {
      centerDestination?: boolean;
      skipFreshGps?: boolean;
      forceReplace?: boolean;
    } = {},
  ): void {
    if (opts.forceReplace === true) {
      addingStop = false;
    }

    if (addingStop) {
      endActiveNavForNewPlan();
      clearProfileRouteCache();

      if (stops.length >= MAX_ROUTE_STOPS) {
        addingStop = false;
        ui.setStatus(t('status.stopsMax'), 'error');
        syncStopsChrome();
        return;
      }
      if (stops.length === 0) {
        stops = [place];
      } else {
        stops = [...stops, place];
      }
      addingStop = false;
    } else {
      resetActiveTrip();
      stops = [place];
    }

    ui.setSearchQuery(place.label);
    ui.setNavState('planned');
    syncSessionWakeLock();
    ui.setInfoExpanded(false);
    map.setStops(
      stops.map((s) => ({ location: s.location, label: s.label })),
      { center: opts.centerDestination === true },
    );
    ui.setStopsUi(stops.map((s) => ({ label: s.label })), {
      addingStop,
      canAdd: stops.length > 0 && stops.length < MAX_ROUTE_STOPS,
    });
    void refreshWeather(place.location);
    void planAndRender({ skipFreshGps: opts.skipFreshGps === true });
  }

  // Thin wrapper: every fix should both render the user marker AND
  // persist so the next fresh-boot starts at the same place.
  function rememberLocation(
    p: LatLng,
    accuracy?: number,
    opts: { moveCamera?: boolean; speedMps?: number | null } = {},
  ): void {
    lastFix = p;
    if (opts.speedMps !== undefined) {
      map.setUserSpeed(opts.speedMps);
    }
    map.setUserLocation(p, accuracy, opts);
    saveLocation(p);
    setGeocodeFocus(p);
    scheduleWeatherRefresh(weatherFocus());
  }

  /**
   * Always-on GPS watch. Single stream for marker, persistence, live HUD,
   * and off-route detection (Rerouter no longer opens its own watch).
   *
   * While a live navigation session owns the camera via followNavigation,
   * idle camera follow stays off that path.
   */
  let locationWatchId: number | null = null;
  function stopLocationWatch(): void {
    if (locationWatchId === null) return;
    navigator.geolocation.clearWatch(locationWatchId);
    locationWatchId = null;
  }
  let lastProgressPaintAt = 0;
  let lastProgressPaintMeters = -1e9;
  let lastGoodNavGps: NavGpsSample | null = null;
  let navCoastTimer: number | null = null;
  let lastNavCoastTickAt = 0;

  function stopNavCoast(): void {
    if (navCoastTimer !== null) {
      window.clearInterval(navCoastTimer);
      navCoastTimer = null;
    }
  }

  function routeAvgSpeedMps(): number {
    const r = lastRouteResult;
    if (r && r.distance > 0 && r.duration > 0) return r.distance / r.duration;
    return 13.3;
  }

  function ensureNavCoast(): void {
    if (!isNavigating || isPaused) return;
    if (navCoastTimer !== null) return;
    lastNavCoastTickAt = Date.now();
    navCoastTimer = window.setInterval(() => {
      tickNavCoast();
    }, 250);
  }

  function tickNavCoast(): void {
    if (!isNavigating || isPaused || !lastGoodNavGps) {
      stopNavCoast();
      return;
    }
    const index = rerouter.getRouteIndex();
    const route = rerouter.getRoute();
    if (!index || route.length < 2) {
      stopNavCoast();
      return;
    }
    const now = Date.now();
    const age = now - lastGoodNavGps.atMs;
    if (age < NAV_GPS_STALE_MS) {
      return;
    }
    const dtSec = Math.max(0.05, (now - lastNavCoastTickAt) / 1000);
    lastNavCoastTickAt = now;
    let speed = lastGoodNavGps.speedMps;
    if (age > NAV_GPS_COAST_MAX_MS) {
      speed = 0;
    }
    const advance = coastAdvanceMeters(speed, routeAvgSpeedMps(), dtSec);
    const traveled = Math.min(
      index.totalMeters,
      lastGoodNavGps.traveledMeters + advance,
    );
    lastGoodNavGps = {
      ...lastGoodNavGps,
      traveledMeters: traveled,
      speedMps: speed,
    };
    applyLiveNavFrame({
      traveledMeters: traveled,
      displayPos: index.interpolate(traveled),
      speedMps: speed > 0.5 ? speed : null,
      accuracyM: lastGoodNavGps.accuracyM,
      rawForPoi: null,
      coasting: true,
    });
  }

  function handleNavGpsError(err: GeolocationPositionError): void {
    if (!isNavigating && !isPaused) return;
    if (err.code === GeolocationPositionError.PERMISSION_DENIED) {
      ui.setStatus(gpsErrorLabel(err), 'error');
    }
    ensureNavCoast();
  }
  function startLocationWatch(): void {
    if (locationWatchId !== null) return;
    if (!('geolocation' in navigator)) return;
    locationWatchId = navigator.geolocation.watchPosition(
      (pos) => {
        const here: LatLng = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        const navActive = isNavigating && !isPaused;
        const speedMps =
          pos.coords.speed !== null && pos.coords.speed > 0.5 ? pos.coords.speed : null;
        if (navActive) {
          handleNavPosition(pos);
          return;
        }
        rememberLocation(here, pos.coords.accuracy, {
          moveCamera: stops.length === 0,
          speedMps,
        });
      },
      (err) => {
        if (isNavigating || isPaused) {
          handleNavGpsError(err);
          return;
        }
        if (err.code === GeolocationPositionError.PERMISSION_DENIED) {
          /* keep silent — avoid toast storms */
        }
      },
      powerSaver
        ? { enableHighAccuracy: false, maximumAge: 8_000, timeout: 20_000 }
        : { enableHighAccuracy: true, maximumAge: 2_000, timeout: 15_000 },
    );
  }
  function restartLocationWatch(): void {
    stopLocationWatch();
    startLocationWatch();
  }

  // ---------------------------------------------------------------
  // POI refresh (OSM cameras + speed limits), throttled.
  // ---------------------------------------------------------------
  //
  // Fires every 5 s of LIVE navigation while the user has moved to a
  // new ~1 km grid cell. fetches are grid-bin cached on the Overpass side
  // (see src/poi.ts), so the actual upstream traffic is one request per
  // grid cell per 30 min. Camera markers are re-set wholesale.

  const POI_REFRESH_INTERVAL_MS = 5_000;
  const AMENITY_REFRESH_INTERVAL_MS = 45_000;
  const POWER_POI_REFRESH_MS = 20_000;
  const POWER_AMENITY_REFRESH_MS = 90_000;
  const POWER_HUD_MIN_MS = 2_200;
  const POWER_DIM_IDLE_MS = 25_000;
  let lastAmenityRefreshAt = 0;
  let cachedAmenities: Amenity[] = [];
  let powerSaver = false;
  let lastHudTickAt = 0;
  let lastManeuverKey = '';
  let powerDimTimer: number | null = null;

  function noteNavEvent(): void {
    ui.setPowerDim(false);
    if (powerDimTimer !== null) {
      window.clearTimeout(powerDimTimer);
      powerDimTimer = null;
    }
    if (powerSaver && isNavigating && !isPaused) {
      powerDimTimer = window.setTimeout(() => {
        if (powerSaver && isNavigating && !isPaused) ui.setPowerDim(true);
      }, POWER_DIM_IDLE_MS);
    }
  }

  function applyPowerSaverMode(on: boolean): void {
    powerSaver = on;
    savePowerSaver(on);
    map.setPowerSaver(on);
    ui.setPowerSaverUi(on);
    document.body.dataset.powerSaver = on ? '1' : '0';
    restartLocationWatch();
    if (on && isNavigating && !isPaused) noteNavEvent();
    else {
      ui.setPowerDim(false);
      if (powerDimTimer !== null) {
        window.clearTimeout(powerDimTimer);
        powerDimTimer = null;
      }
    }
  }

  function amenitiesEnabledForProfile(profile: Profile): boolean {
    return profile === 'driving-car';
  }

  function clearAmenitiesUi(): void {
    cachedAmenities = [];
    lastAmenityRefreshAt = 0;
    map.clearAmenities();
    ui.setHudAmenities({});
  }

  async function refreshAmenities(
    route: LatLng[],
    traveledMeters: number,
  ): Promise<void> {
    if (!amenitiesEnabledForProfile(currentProfile)) {
      clearAmenitiesUi();
      return;
    }
    const list = await fetchAmenitiesAlongRoute(route, traveledMeters);
    cachedAmenities = list;
    map.setAmenities(list);
    const next = pickNextAmenities(list, traveledMeters);
    ui.setHudAmenities({
      ...(next.fuel
        ? { fuel: { distM: Math.max(0, next.fuel.alongMeters - traveledMeters) } }
        : {}),
      ...(next.toilet
        ? {
            toilet: {
              distM: Math.max(0, next.toilet.alongMeters - traveledMeters),
            },
          }
        : {}),
      ...(next.rest
        ? { rest: { distM: Math.max(0, next.rest.alongMeters - traveledMeters) } }
        : {}),
    });
  }

  function paintAmenityDistances(traveledMeters: number): void {
    if (!amenitiesEnabledForProfile(currentProfile)) {
      ui.setHudAmenities({});
      return;
    }
    const next = pickNextAmenities(cachedAmenities, traveledMeters);
    ui.setHudAmenities({
      ...(next.fuel
        ? { fuel: { distM: Math.max(0, next.fuel.alongMeters - traveledMeters) } }
        : {}),
      ...(next.toilet
        ? {
            toilet: {
              distM: Math.max(0, next.toilet.alongMeters - traveledMeters),
            },
          }
        : {}),
      ...(next.rest
        ? { rest: { distM: Math.max(0, next.rest.alongMeters - traveledMeters) } }
        : {}),
    });
  }

  async function refreshPoi(
    here: LatLng,
    velocityBearingDeg: number | null,
    speedMps: number | null,
  ): Promise<void> {
    const data = await fetchNearbyPois(here);
    if (!data) {
      // Abort / network blip: keep last speed limit (and cameras) rather
      // than flashing "—" every grid hop.
      return;
    }
    const radars = extractRadars(data);
    map.setCameras(radars);
    const limit = extractNearestSpeedLimit(data, here);
    if (limit) {
      currentSpeedLimitKmh = limit.kmh;
      lastSpeedLimitAt = { lat: limit.lat, lng: limit.lng };
      ui.setSpeedLimit(currentSpeedLimitKmh);
    } else if (
      currentSpeedLimitKmh !== null &&
      lastSpeedLimitAt &&
      haversineMeters(here, lastSpeedLimitAt) < 250
    ) {
      // Keep the last on-road reading across brief GPS / matching gaps.
    } else {
      currentSpeedLimitKmh = null;
      lastSpeedLimitAt = null;
      ui.setSpeedLimit(null);
    }
    const upcoming = pickRelevantRadar(
      radars,
      here,
      velocityBearingDeg,
      speedMps,
    );
    if (upcoming) {
      const dist = Math.round(
        haversineMeters(here, { lat: upcoming.lat, lng: upcoming.lng }),
      );
      noteNavEvent();
      ui.setHudRadar(
        upcoming.maxspeed !== undefined
          ? { dist, maxspeed: upcoming.maxspeed }
          : { dist },
      );
    } else {
      ui.setHudRadar(null);
    }
    // Roadwork / closure layer — the same Overpass query already carries
    // these tags (see src/poi.ts), so we render without a second network
    // hit. The closure colour outranks the camera orange so the chip
    // hierarchy is "closure > radar > construction".
    const roadworks = extractRoadworks(data);
    map.setRoadworks(roadworks);
    const upcomingRoadwork = pickRelevantRoadwork(
      roadworks,
      here,
      velocityBearingDeg,
      speedMps,
    );
    if (upcomingRoadwork) {
      const dist = Math.round(
        haversineMeters(here, { lat: upcomingRoadwork.lat, lng: upcomingRoadwork.lng }),
      );
      noteNavEvent();
      ui.setRoadwork(upcomingRoadwork.kind, dist);
    } else {
      ui.setRoadwork(null, null);
    }
    // Over-speed hysteresis: ≥3 s sustained above limit + 10 km/h before
    // flashing the warning. Stores when the over-speed first crossed the
    // threshold; drops instantly when speed falls back below.
    const OVERSPEED_GRACE_MS = 3_000;
    if (speedMps !== null && speedMps > 0.5 && currentSpeedLimitKmh !== null) {
      const kmh = speedMps * 3.6;
      if (kmh > currentSpeedLimitKmh + 10) {
        if (overspeedSinceMs === null) overspeedSinceMs = Date.now();
      } else {
        overspeedSinceMs = null;
      }
    } else {
      overspeedSinceMs = null;
    }
    ui.setOverspeed(
      overspeedSinceMs !== null &&
        Date.now() - overspeedSinceMs >= OVERSPEED_GRACE_MS,
    );
  }

  function bindActiveRoute(coords: LatLng[]): void {
    rerouter.setRoute(coords);
    map.setRouteProgressIndex(rerouter.getRouteIndex());
  }

  const rerouter = new Rerouter(
    async (pos) => {
      if (stops.length === 0 || replanning) return;
      try {
        replanning = true;
        const here: LatLng = { lat: pos.lat, lng: pos.lng };
        const route = await getRoute(
          here,
          stops.map((s) => s.location),
          currentProfile,
          routeOptions,
        );
        writeProfileRouteCache(currentProfile, routeOptions, stops, here, route);
        lastRouteResult = route;
        map.drawRoute(route.coords, { fit: false, keepFollow: true });
        bindActiveRoute(route.coords);
        ui.setRoute(route.coords, route.distance, route.duration, route.steps);
        const index = rerouter.getRouteIndex();
        const proj = index?.locate(here);
        if (lastGoodNavGps && proj) {
          lastGoodNavGps = {
            ...lastGoodNavGps,
            rawPos: here,
            traveledMeters: proj.traveledMeters,
            atMs: Date.now(),
          };
        }
        const off = distanceToRouteMeters(here, route.coords);
        ui.setStatus(off < 60 ? t('status.reroutingDone') : t('status.rerouting'));
        if (proj) {
          applyLiveNavFrame({
            traveledMeters: proj.traveledMeters,
            displayPos:
              proj.distMeters < NAV_GPS_ON_ROUTE_M
                ? index!.interpolate(proj.traveledMeters)
                : here,
            speedMps: lastGoodNavGps?.speedMps && lastGoodNavGps.speedMps > 0.5
              ? lastGoodNavGps.speedMps
              : null,
            accuracyM: lastGoodNavGps?.accuracyM ?? 30,
            rawForPoi: here,
            coasting: false,
          });
        }
      } catch (err) {
        ui.setStatus((err as Error).message, 'error');
      } finally {
        replanning = false;
      }
    },
    {
      offRouteMeters: 50,
      cooldownMs: 15_000,
    },
  );

  function applyLiveNavFrame(args: {
    traveledMeters: number;
    displayPos: LatLng;
    speedMps: number | null;
    accuracyM: number;
    rawForPoi: LatLng | null;
    coasting: boolean;
  }): void {
    if (!isNavigating || isPaused || stops.length === 0 || !lastRouteResult) return;
    const route = rerouter.getRoute();
    const index = rerouter.getRouteIndex();
    if (route.length < 2 || !index) return;

    const traveled = Math.max(0, Math.min(args.traveledMeters, index.totalMeters));
    const onRoute = index.interpolate(traveled);
    const proj = index.locate(onRoute);
    const progress = {
      traveledMeters: traveled,
      totalMeters: index.totalMeters,
      segmentIndex: proj.segmentIndex,
    };

    map.setUserLocation(args.displayPos, args.accuracyM, { moveCamera: false });

    const now = Date.now();
    if (
      now - lastProgressPaintAt >= 280 ||
      Math.abs(traveled - lastProgressPaintMeters) >= 12
    ) {
      lastProgressPaintAt = now;
      lastProgressPaintMeters = traveled;
      map.setRouteProgress(route, traveled);
    }

    const remainingDistance = Math.max(0, index.totalMeters - traveled);
    if (maybeHandleArrival(remainingDistance, traveled)) {
      return;
    }

    map.setUserSpeed(args.speedMps);
    const avgSpeedMps = routeAvgSpeedMps();
    const lookAheadM = Math.max(
      30,
      Math.min(90, (args.speedMps ?? avgSpeedMps) * 2.5),
    );
    const routeBearing = index.bearingAhead(traveled, lookAheadM);
    smoothBearingDeg = smaBearing(smoothBearingDeg, routeBearing, 0.28);
    map.setTiltingHeading(smoothBearingDeg);
    const cs = findCurrentStep(progress, lastRouteResult.steps, route);
    if (map.isFollowing()) {
      const lookAt =
        cs?.turnAt ??
        (remainingDistance < 1500 ? primaryDestination()?.location ?? null : null);
      map.followNavigation(args.displayPos, lookAt);
    }
    if (
      args.rawForPoi &&
      Date.now() - lastPoiRefreshAt >
        (powerSaver ? POWER_POI_REFRESH_MS : POI_REFRESH_INTERVAL_MS)
    ) {
      lastPoiRefreshAt = Date.now();
      void refreshPoi(args.rawForPoi, smoothBearingDeg, args.speedMps);
    }
    if (powerSaver && Date.now() - lastHudTickAt < POWER_HUD_MIN_MS) return;
    lastHudTickAt = Date.now();
    const effectiveSpeed = args.speedMps ?? avgSpeedMps;
    const remainingDuration = remainingDistance / Math.max(0.5, effectiveSpeed);
    const eta =
      Number.isFinite(remainingDuration) &&
      remainingDuration > 0 &&
      remainingDuration < 24 * 3600
        ? new Date(Date.now() + remainingDuration * 1000)
        : null;
    const currentStep =
      cs && cs.step.instruction
        ? {
            instruction: cs.step.instruction,
            name: cs.step.name,
            remainingStepMeters: cs.remainingMeters,
            type: cs.step.type,
            exit_number: cs.step.exit_number,
          }
        : null;
    const nextRaw =
      cs && cs.index + 1 < lastRouteResult.steps.length
        ? lastRouteResult.steps[cs.index + 1]
        : null;
    const nextStep =
      nextRaw && nextRaw.instruction
        ? {
            instruction: nextRaw.instruction,
            name: nextRaw.name,
            type: nextRaw.type,
          }
        : null;
    if (
      Date.now() - lastAmenityRefreshAt >
      (powerSaver ? POWER_AMENITY_REFRESH_MS : AMENITY_REFRESH_INTERVAL_MS)
    ) {
      lastAmenityRefreshAt = Date.now();
      void refreshAmenities(route, traveled);
    } else {
      paintAmenityDistances(traveled);
    }
    const maneuverKey = currentStep
      ? `${currentStep.type ?? ''}|${currentStep.instruction}|${nextStep?.instruction ?? ''}`
      : '';
    if (maneuverKey && maneuverKey !== lastManeuverKey) {
      lastManeuverKey = maneuverKey;
      noteNavEvent();
    }
    const hudData: HudData = {
      speedMps: args.speedMps,
      remainingDistance,
      remainingDuration,
      eta,
      currentStep,
      nextStep,
      traveledMeters: traveled,
      totalMeters: index.totalMeters,
    };
    lastHudData = hudData;
    ui.setHudData(hudData);
  }

  function handleNavPosition(pos: GeolocationPosition): void {
    const here: LatLng = { lat: pos.coords.latitude, lng: pos.coords.longitude };
    if (!isNavigating || isPaused || stops.length === 0 || !lastRouteResult) {
      map.setUserLocation(here, pos.coords.accuracy, { moveCamera: true });
      return;
    }
    const route = rerouter.getRoute();
    const index = rerouter.getRouteIndex();
    if (route.length < 2 || !index) return;

    const now = Date.now();
    const accuracyM =
      Number.isFinite(pos.coords.accuracy) && pos.coords.accuracy > 0
        ? pos.coords.accuracy
        : 999;
    const gpsSpeedMps =
      pos.coords.speed !== null && pos.coords.speed > 0.5 ? pos.coords.speed : null;
    const projHit = index.locate(here);
    const accept = shouldAcceptNavGpsFix(
      {
        pos: here,
        accuracyM,
        speedMps: gpsSpeedMps,
        nowMs: now,
      },
      lastGoodNavGps,
    );

    if (!accept) {
      ensureNavCoast();
      return;
    }

    const dtSec = lastGoodNavGps
      ? Math.max(0.25, (now - lastGoodNavGps.atMs) / 1000)
      : 1;
    const onRoute = projHit.distMeters < NAV_GPS_ON_ROUTE_M;
    const traveled = onRoute
      ? clampOnRouteTraveledMeters(
          projHit.traveledMeters,
          lastGoodNavGps?.traveledMeters ?? null,
          dtSec,
          accuracyM,
        )
      : (lastGoodNavGps?.traveledMeters ?? projHit.traveledMeters);
    const speedMps = gpsSpeedMps ?? lastGoodNavGps?.speedMps ?? routeAvgSpeedMps();
    lastGoodNavGps = {
      atMs: now,
      rawPos: here,
      accuracyM,
      speedMps: gpsSpeedMps ?? Math.max(0, speedMps),
      traveledMeters: traveled,
    };
    lastFix = here;
    saveLocation(here);
    setGeocodeFocus(here);
    scheduleWeatherRefresh(weatherFocus());
    rerouter.observe(here);
    ensureNavCoast();
    applyLiveNavFrame({
      traveledMeters: traveled,
      displayPos: onRoute ? index.interpolate(traveled) : here,
      speedMps: gpsSpeedMps,
      accuracyM,
      rawForPoi: here,
      coasting: false,
    });
  }

  /**
   * Same camera framing as the Overview button: full route, north-up,
   * follow off. Retries after resize because profile / HUD layout can
   * change the map pane size in the same tick as the replan.
   */
  function fitRouteLikeOverview(coords: LatLng[]): void {
    if (coords.length < 2) return;
    const run = (): void => {
      map.resize();
      map.showRouteOverview(coords);
      ui.setAutoZoomActive(false);
    };
    run();
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        run();
        window.setTimeout(run, 280);
      });
    });
  }

  function isAbortError(err: unknown): boolean {
    return (
      (err instanceof DOMException && err.name === 'AbortError') ||
      (err instanceof Error && err.name === 'AbortError')
    );
  }

  function beginPlanGeneration(): { signal: AbortSignal; generation: number } {
    planAbort?.abort();
    const ctl = new AbortController();
    planAbort = ctl;
    planGeneration += 1;
    return { signal: ctl.signal, generation: planGeneration };
  }

  async function planAndRender(
    opts: { forceOverview?: boolean; skipFreshGps?: boolean } = {},
  ): Promise<void> {
    const dest = primaryDestination();
    if (!dest || stops.length === 0) {
      ui.setStatus(t('status.searchPrompt'));
      return;
    }

    const { signal, generation } = beginPlanGeneration();
    const profileForPlan = currentProfile;
    const optionsForPlan: RouteOptions = {
      preference: routeOptions.preference,
      avoid: [...routeOptions.avoid],
    };
    const stopsForPlan = stops.map((s) => ({
      location: { lat: s.location.lat, lng: s.location.lng },
      label: s.label,
    }));

    const runPlan = async (start: LatLng): Promise<void> => {
      if (signal.aborted || generation !== planGeneration) return;
      smoothBearingDeg = 0;
      rememberLocation(start, undefined, { moveCamera: false });
      try {
        replanning = true;
        const cached = readProfileRouteCache(
          profileForPlan,
          optionsForPlan,
          stopsForPlan,
          start,
        );
        if (cached) {
          if (signal.aborted || generation !== planGeneration) return;
          applyPlannedRoute(
            cached.route,
            dest,
            profileForPlan,
            { forceOverview: opts.forceOverview },
            cached.alternatives,
          );
          consumePendingDeeplink();
          const wantAlts = !isNavigating && !isPaused;
          const missingAlts =
            wantAlts &&
            profileForPlan === 'driving-car' &&
            cached.alternatives.length === 0;
          if (!cached.complete || missingAlts) {
            void completeCachedPlanInBackground(
              start,
              dest,
              profileForPlan,
              optionsForPlan,
              stopsForPlan,
              signal,
              generation,
              {
                forceOverview: opts.forceOverview,
                persist: !cached.complete,
              },
            );
          }
          return;
        }
        const planned = await planRoutes(
          start,
          stopsForPlan.map((s) => s.location),
          profileForPlan,
          optionsForPlan,
          signal,
          { alternatives: !isNavigating && !isPaused },
        );
        if (signal.aborted || generation !== planGeneration) return;
        writeProfileRouteCache(
          profileForPlan,
          optionsForPlan,
          stopsForPlan,
          start,
          planned.primary,
          true,
          planned.alternatives,
        );
        applyPlannedRoute(
          planned.primary,
          dest,
          profileForPlan,
          { forceOverview: opts.forceOverview },
          planned.alternatives,
        );
        consumePendingDeeplink();
      } catch (err) {
        if (isAbortError(err) || signal.aborted || generation !== planGeneration) return;
        ui.setStatus((err as Error).message, 'error');
      } finally {
        if (generation === planGeneration) replanning = false;
      }
    };

    const fallbackStart = (): LatLng | null => lastFix ?? loadSavedLocation();

    // Profile / options switches: last known fix is enough — do not wait on GPS.
    const warmStart = fallbackStart();
    if (warmStart) {
      const cached = readProfileRouteCache(
        profileForPlan,
        optionsForPlan,
        stopsForPlan,
        warmStart,
      );
      if (cached || opts.skipFreshGps) {
        ui.setStatus(t('status.routePlanning'));
        await runPlan(warmStart);
        return;
      }
    }

    if (!('geolocation' in navigator)) {
      const start = fallbackStart();
      if (!start) {
        ui.setStatus(t('status.gpsUnsupported'), 'error');
        return;
      }
      ui.setStatus(t('status.routePlanning'));
      await runPlan(start);
      return;
    }

    try {
      if (navigator.permissions?.query) {
        const status = await navigator.permissions.query({ name: 'geolocation' });
        if (signal.aborted || generation !== planGeneration) return;
        if (status.state === 'denied') {
          const start = fallbackStart();
          if (start) {
            ui.setStatus(t('status.routePlanning'));
            await runPlan(start);
            return;
          }
          ui.setStatus(t('status.gpsDeniedHelp'), 'error');
          return;
        }
      }
    } catch {
      /* permissions.query unsupported — fall through */
    }

    if (signal.aborted || generation !== planGeneration) return;
    ui.setStatus(t('status.routePlanning'));
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (signal.aborted || generation !== planGeneration) return;
        void runPlan({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      (err) => {
        if (signal.aborted || generation !== planGeneration) return;
        const start = fallbackStart();
        if (start) {
          void runPlan(start);
          return;
        }
        ui.setStatus(gpsErrorLabel(err), 'error');
      },
      { enableHighAccuracy: true, timeout: 12_000 },
    );
  }

  /**
   * Phase B — promote a planned route into a live navigation session.
   * Idempotent: safe to call repeatedly while already navigating.
   */
  function startNavigation(): void {
    if (isNavigating || isPaused || stops.length === 0) return;
    isNavigating = true;
    isPaused = false;
    lastGoodNavGps = null;
    stopNavCoast();
    routeAlternatives = [];
    map.clearAlternativeRoutes();
    rerouter.start();
    map.setLiveNavigation(true);
    ui.setNavState('navigating');
    syncStopsChrome();
    map.recenterOnUser();
    const r = lastRouteResult;
    if (r && r.coords.length >= 2) {
      if (amenitiesEnabledForProfile(currentProfile)) {
        lastAmenityRefreshAt = 0;
        void refreshAmenities(r.coords, 0);
      } else {
        clearAmenitiesUi();
      }
    }
    noteNavEvent();
    const hudData: HudData = {
      speedMps: null,
      remainingDistance: r?.distance ?? 0,
      remainingDuration: r?.duration ?? 0,
      eta:
        r && r.duration > 0 && r.duration < 24 * 3600
          ? new Date(Date.now() + r.duration * 1000)
          : null,
      currentStep: null,
      nextStep: null,
      traveledMeters: 0,
      totalMeters: r?.distance ?? 0,
    };
    lastHudData = hudData;
    ui.setHudData(hudData);
    ui.setStatus(t('status.navActive'));
    syncSessionWakeLock();
  }

  /**
   * Suspend the live session but KEEP the polyline, the steps list and the
   * destination marker. The HUD displays the last computed values so the
   * user can see how far they had to go at the moment of pausing.
   */
  function pauseNavigation(): void {
    if (!isNavigating || isPaused) return;
    isPaused = true;
    stopNavCoast();
    rerouter.stop();
    ui.setNavState('paused');
    // Re-render the last known HUD snapshot so paused stats persist.
    if (lastHudData) ui.setHudData(lastHudData);
    ui.setStatus(t('status.navPaused'));
    syncSessionWakeLock();
  }

  /**
   * Resume a paused session. The existing polyline + destination are kept;
   * the shared GPS watch keeps feeding fixes (Rerouter listening resumes).
   */
  function resumeNavigation(): void {
    if (!isPaused || stops.length === 0) return;
    isPaused = false;
    lastHudData = null;
    rerouter.start();
    map.setLiveNavigation(true);
    ui.setNavState('navigating');
    map.recenterOnUser();
    ui.setStatus(t('status.navResumed'));
    syncSessionWakeLock();
  }

  /**
   * End the live session but KEEP the polyline, the steps list and the
   * destination marker so the user can still inspect the route preview.
   * Idempotent if called from both 'navigating' and 'paused' states.
   */
  function stopNavigation(): void {
    if (!isNavigating && !isPaused) return;
    endLiveNavigationSession();
    ui.setStatus(t('status.navStopped'));
  }

  function arriveAtDestination(): void {
    if (!isNavigating && !isPaused) return;
    const dest = primaryDestination();
    endLiveNavigationSession();
    const label = dest?.label?.trim();
    ui.setStatus(
      label ? `${t('status.arrivedAt')}${label}` : t('status.arrived'),
    );
  }

  function endLiveNavigationSession(): void {
    stopNavCoast();
    lastGoodNavGps = null;
    rerouter.stop();
    isNavigating = false;
    isPaused = false;
    map.setLiveNavigation(false);
    lastHudData = null;
    ui.setHudData({
      speedMps: null,
      remainingDistance: 0,
      remainingDuration: 0,
      eta: null,
      currentStep: null,
      nextStep: null,
      traveledMeters: 0,
      totalMeters: 0,
    });
    clearAmenitiesUi();
    ui.setPowerDim(false);
    if (powerDimTimer !== null) {
      window.clearTimeout(powerDimTimer);
      powerDimTimer = null;
    }
    ui.setNavState('planned');
    syncSessionWakeLock();
  }

  /** True when the live session was ended because the destination was reached. */
  function maybeHandleArrival(
    remainingDistance: number,
    traveledMeters: number,
  ): boolean {
    // Avoid false positives on parallel roads / ramps: arrival should be
    // route-progress based, not mere beeline proximity to the destination.
    if (traveledMeters < 60) return false;
    if (remainingDistance > 25) return false;
    arriveAtDestination();
    return true;
  }

  ui.on('changeProfile', (profile: Profile) => {
    currentProfile = profile;
    ui.setRouteOptionsUi(routeOptions, currentProfile);
    if (!amenitiesEnabledForProfile(currentProfile)) clearAmenitiesUi();
    if (stops.length === 0) return;

    const dest = primaryDestination();
    const start = lastFix ?? loadSavedLocation();
    const cached = readProfileRouteCache(
      currentProfile,
      routeOptions,
      stops,
      start,
      { ignoreStartDrift: true },
    );
    // Abort in-flight foot/bike ORS immediately; paint button first.
    const pending = beginPlanGeneration();
    window.requestAnimationFrame(() => {
      if (currentProfile !== profile) return;
      if (cached && dest) {
        replanning = false;
        applyPlannedRoute(cached.route, dest, currentProfile, {
          forceOverview: true,
          persist: false,
        });
        if (!cached.complete) {
          void completeCachedPlanInBackground(
            start ?? cached.start,
            dest,
            currentProfile,
            routeOptions,
            stops,
            pending.signal,
            pending.generation,
            { forceOverview: true, persist: false },
          );
        }
        return;
      }
      void planAndRender({ forceOverview: true, skipFreshGps: true });
    });
  });

  ui.on('routeOptions', (opts: RouteOptions) => {
    routeOptions = {
      preference: opts.preference,
      avoid: [...opts.avoid],
    };
    saveRouteOptions(routeOptions);
    if (stops.length === 0) return;

    const dest = primaryDestination();
    const start = lastFix ?? loadSavedLocation();
    const cached = readProfileRouteCache(
      currentProfile,
      routeOptions,
      stops,
      start,
      { ignoreStartDrift: true },
    );
    const pending = beginPlanGeneration();
    window.requestAnimationFrame(() => {
      if (cached && dest) {
        replanning = false;
        applyPlannedRoute(cached.route, dest, currentProfile, { persist: false });
        if (!cached.complete) {
          void completeCachedPlanInBackground(
            start ?? cached.start,
            dest,
            currentProfile,
            routeOptions,
            stops,
            pending.signal,
            pending.generation,
            { persist: false },
          );
        }
        return;
      }
      void planAndRender({ skipFreshGps: true });
    });
  });

  ui.on('search', async (query: string) => {
    // Arrow / form submit: drop the previous trip immediately, then plan anew.
    const pickGen = ++searchPickGeneration;
    addingStop = false;
    resetActiveTrip();
    syncStopsChrome();
    ui.setNavState('hidden');
    try {
      const hits = await geocode(query);
      if (pickGen !== searchPickGeneration) return;
      if (hits.length === 0) {
        ui.setStatus(t('status.noResult'), 'error');
        return;
      }
      const first = hits[0]!;
      applyPickedPlace({ location: first.location, label: first.name });
    } catch (err) {
      if (pickGen !== searchPickGeneration) return;
      ui.setStatus((err as Error).message, 'error');
    }
  });

  function goToPlace(place: SavedPlace, opts: { centerDestination?: boolean } = {}): void {
    searchPickGeneration += 1;
    applyPickedPlace(
      {
        location: { lat: place.lat, lng: place.lng },
        label: placeDisplayName(place),
      },
      {
        centerDestination: opts.centerDestination !== false,
        skipFreshGps: true,
        forceReplace: true,
      },
    );
  }

  function restoreCachedRoute(): void {
    const cached = loadLastRoute();
    if (!cached) {
      ui.setStatus(t('fav.noLast'), 'error');
      return;
    }
    const route: RouteResult = {
      coords: cached.route.coords,
      distance: cached.route.distance,
      duration: cached.route.duration,
      steps: cached.route.steps,
    };
    stops = [
      {
        location: { lat: cached.destination.lat, lng: cached.destination.lng },
        label: cached.destination.label,
      },
    ];
    addingStop = false;
    lastRouteResult = route;
    routeAlternatives = [];
    endActiveNavForNewPlan();
    syncStopsChrome();
    map.drawRoute(route.coords, { fit: false });
    map.clearAlternativeRoutes();
    bindActiveRoute(route.coords);
    ui.setRoute(route.coords, route.distance, route.duration, route.steps);
    ui.setNavState('planned');
    syncSessionWakeLock();
    ui.setInfoExpanded(false);
    fitRouteLikeOverview(route.coords);
    ui.setStatus(t('fav.restored'));
    ui.refreshFavorites();
    consumePendingDeeplink();
  }

  const PENDING_DEEP_KEY = 'navi.pendingDeeplink';

  function parseCoordPair(raw: string): { lat: number; lng: number } | null {
    const [latRaw, lngRaw] = raw.split(',');
    const lat = Number(latRaw);
    const lng = Number(lngRaw);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    return { lat, lng };
  }

  function placeFromDeeplinkParams(params: URLSearchParams): SavedPlace | null {
    const to = params.get('to');
    if (!to) return null;
    const coords = parseCoordPair(to);
    if (!coords) return null;
    const labelRaw = params.get('label');
    let label =
      labelRaw && labelRaw.trim()
        ? labelRaw.trim()
        : `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`;
    try {
      if (/%[0-9A-Fa-f]{2}/.test(label)) {
        label = decodeURIComponent(label);
      }
    } catch {
      /* keep raw label — do not fail the whole deeplink */
    }
    return { lat: coords.lat, lng: coords.lng, label };
  }

  function readStoredDeeplink(): { place: SavedPlace; profile: Profile | null } | null {
    try {
      const raw = sessionStorage.getItem(PENDING_DEEP_KEY);
      if (!raw) return null;
      const parsed: unknown = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      const rec = parsed as Record<string, unknown>;
      const lat = Number(rec.lat);
      const lng = Number(rec.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
      const label =
        typeof rec.label === 'string' && rec.label.trim()
          ? rec.label.trim()
          : `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      const profileRaw = rec.profile;
      const profile =
        profileRaw === 'driving-car' ||
        profileRaw === 'cycling-regular' ||
        profileRaw === 'foot-walking'
          ? profileRaw
          : null;
      return { place: { lat, lng, label }, profile };
    } catch {
      return null;
    }
  }

  function stashPendingDeeplink(place: SavedPlace, profile: Profile): void {
    try {
      sessionStorage.setItem(
        PENDING_DEEP_KEY,
        JSON.stringify({
          lat: place.lat,
          lng: place.lng,
          label: place.label,
          profile,
        }),
      );
    } catch {
      /* private mode / quota — URL may still work for this load */
    }
  }

  function consumePendingDeeplink(): void {
    try {
      sessionStorage.removeItem(PENDING_DEEP_KEY);
    } catch {
      /* ignore */
    }
  }

  function applySharedProfile(profile: Profile): void {
    currentProfile = profile;
    document.querySelectorAll<HTMLButtonElement>('.profile').forEach((btn) => {
      const active = btn.dataset.profile === profile;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', String(active));
    });
    ui.setRouteOptionsUi(routeOptions, currentProfile);
  }

  /** URL query first, then sessionStorage (survives SW reload after URL clear). */
  function resolveDeeplink(): SavedPlace | null {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = placeFromDeeplinkParams(params);
      if (fromUrl) {
        const profileRaw = params.get('profile');
        if (
          profileRaw === 'driving-car' ||
          profileRaw === 'cycling-regular' ||
          profileRaw === 'foot-walking'
        ) {
          applySharedProfile(profileRaw);
        }
        stashPendingDeeplink(fromUrl, currentProfile);
        clearDeeplinkFromUrl();
        return fromUrl;
      }
    } catch {
      /* fall through to stored */
    }
    const stored = readStoredDeeplink();
    if (!stored) return null;
    if (stored.profile) applySharedProfile(stored.profile);
    return stored.place;
  }

  function clearDeeplinkFromUrl(): void {
    try {
      const url = new URL(window.location.href);
      if (
        !url.searchParams.has('to') &&
        !url.searchParams.has('label') &&
        !url.searchParams.has('profile')
      ) {
        return;
      }
      url.searchParams.delete('to');
      url.searchParams.delete('label');
      url.searchParams.delete('profile');
      const q = url.searchParams.toString();
      window.history.replaceState({}, '', `${url.pathname}${q ? `?${q}` : ''}${url.hash}`);
    } catch {
      /* ignore */
    }
  }

  ui.on('favoriteGo', (place) => {
    goToPlace(place);
  });

  ui.on('favoriteSet', (slot, place) => {
    void (async () => {
      const dest = primaryDestination();
      let resolved: SavedPlace | null = place
        ? { lat: place.lat, lng: place.lng, label: place.label }
        : null;

      if (!resolved && lastFix) {
        ui.setStatus(t('fav.resolvingAddress'));
        let label: string;
        try {
          label = await reverseGeocode(lastFix);
        } catch {
          label = `${lastFix.lat.toFixed(5)}, ${lastFix.lng.toFixed(5)}`;
        }
        resolved = { lat: lastFix.lat, lng: lastFix.lng, label };
      } else if (!resolved && dest) {
        resolved = {
          lat: dest.location.lat,
          lng: dest.location.lng,
          label: dest.label,
        };
      }

      if (!resolved) {
        ui.setStatus(t('fav.needLocation'), 'error');
        return;
      }
      setFavoriteSlot(slot, resolved);
      ui.refreshFavorites();
      ui.setStatus(t(slot === 'home' ? 'fav.homeSaved' : 'fav.workSaved'));
    })();
  });

  ui.on('favoriteSave', (place) => {
    addSavedPlace({
      lat: place.lat,
      lng: place.lng,
      label: place.label,
      ...(place.name?.trim() ? { name: place.name.trim() } : {}),
    });
    ui.refreshFavorites();
    ui.setStatus(t('fav.saved'));
  });

  ui.on('favoriteRename', (place) => {
    const suggested = place.name?.trim() || place.label.split(',')[0]?.trim() || place.label;
    const raw = window.prompt(t('fav.namePrompt'), suggested);
    if (raw === null) return;
    renameSavedPlace(place, raw.trim() || null);
    ui.refreshFavorites();
    ui.setStatus(t('fav.renamed'));
  });

  ui.on('favoriteRemove', (place) => {
    removeSavedPlace(place);
    ui.refreshFavorites();
  });

  ui.on('favoriteClearSlot', (slot) => {
    setFavoriteSlot(slot, null);
    ui.refreshFavorites();
  });

  ui.on('restoreLastRoute', () => {
    restoreCachedRoute();
  });

  ui.on('shareDestination', async () => {
    const dest = primaryDestination();
    if (!dest) {
      ui.setStatus(t('fav.needDestination'), 'error');
      return;
    }
    const url = new URL(window.location.href);
    url.search = '';
    url.hash = '';
    url.searchParams.set(
      'to',
      `${dest.location.lat.toFixed(6)},${dest.location.lng.toFixed(6)}`,
    );
    url.searchParams.set('label', dest.label);
    url.searchParams.set('profile', currentProfile);
    const link = url.toString();
    const title = 'Navi';
    const text = dest.label;

    const tryNativeShare = async (): Promise<boolean> => {
      if (typeof navigator.share !== 'function') return false;
      const payloads: ShareData[] = [
        { title, text, url: link },
        { title, url: link },
        { title, text: `${text}\n${link}` },
      ];
      for (const data of payloads) {
        try {
          if (typeof navigator.canShare === 'function' && !navigator.canShare(data)) {
            continue;
          }
          await navigator.share(data);
          return true;
        } catch (err) {
          if ((err as Error).name === 'AbortError') return true;
        }
      }
      return false;
    };

    try {
      if (await tryNativeShare()) return;
      await navigator.clipboard.writeText(link);
      ui.setStatus(t('info.shareDone'));
    } catch {
      ui.setStatus(t('info.shareFail'), 'error');
    }
  });

  ui.on('searchResult', (hit: GeocodeHit) => {
    const placeOnly =
      hit.layer === 'locality' ||
      hit.layer === 'localadmin' ||
      hit.layer === 'neighbourhood' ||
      hit.layer === 'borough' ||
      hit.layer === 'county' ||
      hit.layer === 'region';

    if (placeOnly) {
      setGeocodeFocus(hit.location);
      ui.setStatus(t('status.placeRefine'));
      return;
    }

    applyPickedPlace({ location: hit.location, label: hit.name });
  });

  ui.on('addStop', () => {
    if (addingStop) {
      addingStop = false;
      syncStopsChrome();
      ui.setStatus(t('status.multiStopReady'));
      return;
    }
    if (stops.length === 0) {
      ui.setStatus(t('status.addStopNeedDest'), 'error');
      return;
    }
    if (stops.length >= MAX_ROUTE_STOPS) {
      ui.setStatus(t('status.stopsMax'), 'error');
      return;
    }
    addingStop = true;
    syncStopsChrome();
    ui.setStatus(t('status.addStopArmed'));
  });

  ui.on('removeStop', (index) => {
    if (index < 0 || index >= stops.length) return;
    endActiveNavForNewPlan();
    clearProfileRouteCache();
    stops = stops.filter((_, i) => i !== index);
    addingStop = false;
    if (stops.length === 0) {
      lastRouteResult = null;
      routeAlternatives = [];
      clearProfileRouteCache();
      map.clearRoute();
      bindActiveRoute([]);
      ui.clearRoute();
      ui.setNavState('hidden');
      syncStopsChrome();
      syncSessionWakeLock();
      ui.setStatus(t('status.searchPrompt'));
      return;
    }
    syncStopsChrome();
    ui.setNavState('planned');
    syncSessionWakeLock();
    void planAndRender();
  });

  ui.on('autozoom', () => {
    map.recenterOnUser(lastFix ?? undefined);
    ui.setAutoZoomActive(true);
    if (!('geolocation' in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const here: LatLng = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        rememberLocation(here, pos.coords.accuracy, { moveCamera: false });
        map.recenterOnUser(here);
        ui.setAutoZoomActive(true);
      },
      (err) => ui.setStatus(gpsErrorLabel(err), 'error'),
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 1_000 },
    );
  });

  ui.on('powerSaver', (on: boolean) => {
    applyPowerSaverMode(on);
  });

  document.addEventListener(
    'pointerdown',
    () => {
      if (powerSaver && isNavigating && !isPaused) noteNavEvent();
    },
    { passive: true },
  );

  ui.on('clearRoute', () => {
    resetActiveTrip();
    ui.clearRoute();
    syncStopsChrome();
    ui.setNavState('hidden');
    void refreshWeather(weatherFocus());
  });

  ui.on('navtoggle', () => {
    if (isNavigating || isPaused) stopNavigation();
    else startNavigation();
  });

  ui.on('overview', () => {
    const liveRoute = rerouter.getRoute();
    const coords =
      (isNavigating || isPaused) && liveRoute.length >= 2
        ? liveRoute
        : lastRouteResult?.coords ?? liveRoute;
    if (coords.length < 2) {
      ui.setStatus(t('status.noResult'), 'error');
      return;
    }
    fitRouteLikeOverview(coords);
    ui.setStatus(t('hud.action.overviewHint'));
  });

  ui.on('pausetoggle', () => {
    if (isPaused) resumeNavigation();
    else if (isNavigating) pauseNavigation();
  });

  ui.on('viewmode', (mode: ViewMode) => {
    viewMode = mode;
    saveViewMode(viewMode);
    map.setViewMode(viewMode);
    if (isNavigating && !isPaused) {
      map.setTiltingHeading(smoothBearingDeg);
    }
    ui.setArrowBearing(map.map.getBearing());
  });

  // Initial GPS fix → user marker only (no route yet).
  //
  // Mirrors the proactive permissions.query pre-flight used by
  // planAndRender(): if the user previously hit "Block" (or the OS-level
  // permission is off), show the instructive message up-front instead of
  // silently swallowing the DENIED error. Safari iOS throws on
  // 'permissions.query({name:"geolocation"})'; the try/catch falls back
  // to the legacy getCurrentPosition flow so those users still get a
  // fix attempt.
  // Deeplink must win over the idle "enable GPS" toast.
  const deep = resolveDeeplink();
  if ('geolocation' in navigator) {
    void (async () => {
      try {
        if (navigator.permissions?.query) {
          const status = await navigator.permissions.query({ name: 'geolocation' });
          if (status.state === 'denied') {
            if (!deep && stops.length === 0) {
              ui.setStatus(t('status.gpsDeniedHelp'), 'error');
            }
            return;
          }
        }
      } catch {
        /* permissions.query unsupported — fall through to legacy fix */
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          rememberLocation(
            { lat: pos.coords.latitude, lng: pos.coords.longitude },
            pos.coords.accuracy,
            { moveCamera: !deep && stops.length === 0 },
          );
          startLocationWatch();
        },
        (err) => {
          if (err.code === GeolocationPositionError.PERMISSION_DENIED) {
            if (!deep && stops.length === 0) {
              ui.setStatus(t('status.gpsDeniedHelp'), 'error');
            }
            return;
          }
          startLocationWatch();
        },
        { enableHighAccuracy: false, timeout: 8_000 },
      );
    })();
  }

  // Apply the user's preferred view mode on boot so the topbar toggle
  // and the body data-view-mode attribute are correct from the very
  // first paint.
  viewMode = loadSavedViewMode();
  map.setViewMode(viewMode);
  ui.setViewMode(viewMode);
  applyPowerSaverMode(loadSavedPowerSaver());
  ui.setRouteOptionsUi(routeOptions, currentProfile);
  // Prime the camera-bearing mirror so the first tilted-mode toggle
  // doesn't snap from 0°.
  if (viewMode === 'tilted' || viewMode === '2d') {
    map.setTiltingHeading(smoothBearingDeg);
  }
  if (typeof window !== 'undefined') {
    const host = window as typeof window & {
      __naviDebug?: {
        geocodeQuota: () => ReturnType<typeof getGeocodeQuotaDebug>;
        geocodeCache: () => ReturnType<typeof getGeocodeCacheDebug>;
      };
    };
    host.__naviDebug = {
      geocodeQuota: () => getGeocodeQuotaDebug(),
      geocodeCache: () => getGeocodeCacheDebug(),
    };
  }

  ui.refreshFavorites();

  if (deep) {
    ui.setStatus(t('status.deeplink'));
    goToPlace(deep, { centerDestination: true });
  } else if (!navigator.onLine && loadLastRoute()) {
    ui.setStatus(t('status.offlineRoute'));
    ui.refreshFavorites();
  } else {
    ui.setStatus(t('status.searchPrompt'));
  }

  window.addEventListener('online', () => {
    ui.refreshFavorites();
  });
  window.addEventListener('offline', () => {
    if (loadLastRoute()) {
      ui.setStatus(t('status.offlineRoute'));
      ui.refreshFavorites();
    }
  });
}

bootstrap().catch((err) => {
  // eslint-disable-next-line no-console
  console.error(err);
  document.body.insertAdjacentHTML(
    'beforeend',
    `<p style="position:fixed;inset:0;display:grid;place-items:center;background:#0f172a;color:#f87171;font:14px sans-serif;padding:16px;text-align:center">${String(err)}</p>`,
  );
});
